import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, RefreshCw, Radio, LogOut, Download, Bell, BellOff,
  Volume2, VolumeX, Tag as TagIcon, Home, Sun, Moon, DownloadCloud, DollarSign, MessageCircle,
  PanelLeftClose, PanelLeftOpen, Settings
} from 'lucide-react';

const SIDEBAR_STORAGE_KEY = 'crm_sidebar_collapsed';

const SidebarItem = ({
  icon: Icon,
  label,
  onClick,
  active = false,
  badge,
  danger = false,
  accent = false,
  disabled = false,
  badgeText,
  collapsed = false,
  testId,
}) => {
  const hasBadge = (typeof badge === 'number' && badge > 0) || Boolean(badgeText);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
      data-testid={testId}
      className={`relative flex w-full items-center rounded-lg text-[14px] font-semibold transition-colors ${
        collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'
      } ${
        disabled
          ? 'opacity-40 cursor-not-allowed'
          : danger
            ? 'text-[#8892a4] hover:text-red-300 hover:bg-red-500/10'
            : accent
              ? 'text-emerald-300 hover:bg-emerald-500/10'
              : active
                ? 'bg-emerald-500/15 text-emerald-300'
                : 'text-[#c3ccdb] hover:text-white hover:bg-slate-800/70'
      }`}
    >
      <Icon className="w-5 h-5 shrink-0" strokeWidth={2} />

      {collapsed ? (
        hasBadge && (
          <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-emerald-400" />
        )
      ) : (
        <>
          <span className="flex-1 truncate text-left">{label}</span>

          {typeof badge === 'number' && badge > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-emerald-500 text-[#04100a] text-[10px] font-bold">
              {badge > 99 ? '99+' : badge}
            </span>
          )}

          {badgeText && (
            <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-400 to-cyan-400 text-[#04100a] text-[9px] font-black uppercase tracking-wider">
              {badgeText}
            </span>
          )}
        </>
      )}
    </button>
  );
};

export const SidebarNav = ({
  currentUser,
  darkMode,
  onThemeToggle,
  onFunnelOpen,
  soundEnabled,
  onSoundToggle,
  notifyEnabled,
  onNotifyToggle,
  pwaPrompt,
  pwaInstalled,
  onInstall,
  onBroadcast,
  onTagsOpen,
  onMPInboxOpen,
  mpInboxUnread = 0,
  mpInboxTotalPending = 0,
  onRefresh,
  onContactsExport,
  unreadCount = 0,
  onBackToLines,
  showBackToLines = false,
}) => {
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? '1' : '0'); } catch { /* silent */ }
  }, [collapsed]);

  const handleLogout = () => {
    // Login.js guarda 'token' y 'role' (no 'access_token'); si no se limpian, la sesión sigue viva.
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('role');
    } catch { /* silent */ }
    navigate('/login');
  };

  const canBroadcast = currentUser?.role === 'admin' || (currentUser?.line_ids && currentUser.line_ids.length > 0);

  return (
    <aside
      data-testid="crm-sidebar"
      data-collapsed={collapsed ? 'true' : 'false'}
      className={`hidden shrink-0 flex-col gap-1 overflow-x-hidden overflow-y-auto border-r border-slate-800 bg-[#0f1420] transition-[width,padding] duration-200 md:flex ${
        collapsed ? 'w-[64px] px-2 py-4' : 'w-[220px] p-4'
      }`}
    >

      {!collapsed && (
        <div className="flex items-center pb-4 pt-1">
          <div className="select-none text-[20px] font-black tracking-wide">
            BLACK<span className="text-emerald-400">GUARDIAN</span>
          </div>
        </div>
      )}

      {showBackToLines && (
        <SidebarItem icon={Home} label="Volver a líneas" onClick={onBackToLines} collapsed={collapsed} />
      )}

      <SidebarItem
        icon={MessageCircle}
        label="CRM IA"
        onClick={() => {}}
        active={true}
        badgeText="ELITE"
        collapsed={collapsed}
      />

      <SidebarItem
        icon={RefreshCw}
        label="Actualizar"
        onClick={onRefresh}
        badge={unreadCount}
        collapsed={collapsed}
      />

      <SidebarItem
        icon={BarChart3}
        label="Embudo"
        onClick={onFunnelOpen}
        collapsed={collapsed}
      />

      {canBroadcast && (
        <SidebarItem
          icon={Radio}
          label="Envío masivo"
          onClick={onBroadcast}
          accent
          collapsed={collapsed}
        />
      )}

      {canBroadcast && (
        <SidebarItem
          icon={TagIcon}
          label="Etiquetas"
          onClick={onTagsOpen}
          collapsed={collapsed}
        />
      )}

      {onMPInboxOpen && (
        <SidebarItem
          icon={DollarSign}
          label="Mercado Pago"
          onClick={onMPInboxOpen}
          badge={mpInboxUnread || mpInboxTotalPending}
          active={mpInboxUnread > 0}
          accent
          collapsed={collapsed}
        />
      )}

      {/* Acceso a "Mi configuración" desde el CRM: en esta vista el nav
          global está oculto (index.css), así que este es el acceso del cajero. */}
      <SidebarItem
        icon={Settings}
        label="Mi config"
        onClick={() => navigate('/mi-configuracion')}
        collapsed={collapsed}
        testId="crm-sidebar-mi-config"
      />

      <div className={`my-2 h-px bg-slate-800 ${collapsed ? 'mx-0' : 'mx-2'}`} />

      <SidebarItem
        icon={soundEnabled ? Volume2 : VolumeX}
        label={soundEnabled ? 'Silenciar' : 'Sonido'}
        onClick={onSoundToggle}
        collapsed={collapsed}
      />

      <SidebarItem
        icon={notifyEnabled ? Bell : BellOff}
        label={notifyEnabled ? 'Alertas Off' : 'Alertas On'}
        onClick={onNotifyToggle}
        collapsed={collapsed}
      />

      {onContactsExport && currentUser?.role === 'admin' && (
        <SidebarItem
          icon={Download}
          label="Exportar"
          onClick={onContactsExport}
          collapsed={collapsed}
        />
      )}

      {pwaPrompt && !pwaInstalled && (
        <SidebarItem
          icon={DownloadCloud}
          label="Instalar App"
          onClick={onInstall}
          accent
          collapsed={collapsed}
        />
      )}

      <div className="flex-1" />

      <SidebarItem
        icon={collapsed ? PanelLeftOpen : PanelLeftClose}
        label={collapsed ? 'Expandir menú' : 'Contraer menú'}
        onClick={() => setCollapsed((v) => !v)}
        collapsed={collapsed}
        testId="crm-sidebar-toggle"
      />

      {onThemeToggle && (
        <SidebarItem
          icon={darkMode ? Sun : Moon}
          label={darkMode ? 'Modo claro' : 'Modo oscuro'}
          onClick={onThemeToggle}
          collapsed={collapsed}
        />
      )}

      <SidebarItem
        icon={LogOut}
        label="Salir"
        onClick={handleLogout}
        danger
        collapsed={collapsed}
      />
    </aside>
  );
};
